import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { extractErrorMessage } from '../../utils/errorHandler';

interface ReturnDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (comment: string) => Promise<void>;
  title: string;
  targetName?: string;
  maxLength?: number;
}

export const ReturnDialog: React.FC<ReturnDialogProps> = ({
  open,
  onClose,
  onSubmit,
  title,
  targetName,
  maxLength = 1000,
}) => {
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    if (!loading) {
      setComment('');
      setError('');
      onClose();
    }
  };

  const handleSubmit = async () => {
    // バリデーション
    if (!comment.trim()) {
      setError('差し戻し理由を入力してください');
      return;
    }

    if (comment.length > maxLength) {
      setError(`差し戻し理由は${maxLength}文字以内で入力してください`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onSubmit(comment);
      setComment('');
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err, '差し戻し処理に失敗しました'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ArrowBack color="warning" />
          <span>{title}</span>
        </Box>
      </DialogTitle>

      <DialogContent>
        {targetName && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {targetName}の評価を差し戻します
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          差し戻しの理由を具体的に記入してください。記入された内容は差し戻し先の担当者に通知されます。
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          multiline
          rows={6}
          label="差し戻し理由"
          placeholder="差し戻しの理由を詳しく記入してください&#10;&#10;例：&#10;・評価項目「B-3」の評価点が自己評価と大きく異なるため、再確認をお願いします。&#10;・定量データの入力に誤りがあるため、修正をお願いします。"
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
            setError('');
          }}
          disabled={loading}
          required
          helperText={`${comment.length} / ${maxLength}文字`}
          inputProps={{
            maxLength: maxLength,
          }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          キャンセル
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="warning"
          disabled={loading || !comment.trim()}
          startIcon={<ArrowBack />}
        >
          {loading ? '差し戻し中...' : '差し戻す'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
